import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProgressLine from 'src/components/ProgressLine';
import {
    CAP_DEVELOPMENT,
    CAP_IMPLEMENTATION,
    CAP_COMPLETED,
    CAP_PROCESS_COMPLETE,
    DEVELOPMENT_SUPPORT
} from 'src/utils/data/job';
import s from './CapboxProgress.styles';

const CapboxProgress = ({
    findings, 
    jobStatus, 
    overallCapStatus,
    capType
}) => {
    const { t } = useTranslation();
    const [overallCapProgress, setOverallCapProgress] = useState('');
    const [capProgressDetails, setCapProgressDetails] = useState({ openCount: 0, approvedOrClosedLabel: 'COLUMN_STATUS', approvedOrClosedCount: 0 });
    const [capProgressBar, setCapProgressBar] = useState([]);
    const [overallCapProgressTitle, setOverallCapProgressTitle] = useState('CAP_PROGRESS');

    const findPercentage = (actionStatus) => {
        const findingsLength = findings?.length;
        return Math.round(((findings?.filter(f => (f.status.toLowerCase() === actionStatus)).length) * 100) / findingsLength);
    }

    const updateCapProgress = (overallProgress) => {
        let progressBarArr = [];
        if (overallProgress === 'development') {

            // Open (based on finding status): Number of 'Review pending' + number of 'Revision requested'
            const openCount = findings?.filter(f => (f.status.toLowerCase() === 'open' || f.status.toLowerCase() === 'review_pending' || f.status.toLowerCase() === 'revision_requested')).length;
            // Approved (based on finding status): Number of ‘Approved’
            const approvedCount = findings?.filter(f => (f.status.toLowerCase() === 'approved')).length;

            setCapProgressDetails({
                openCount,
                approvedOrClosedLabel: 'APPROVED',
                approvedOrClosedCount: approvedCount
            });

            progressBarArr = [{
                percentage: `${findPercentage('review_pending') + findPercentage('open')}%`,
                color: "var(--dark-blue)",
                label: t('REVIEW_PENDING')
            },
            {
                percentage: `${findPercentage('revision_requested')}%`,
                color: "var(--plum-100)",
                label: t('REVISION_REQUESTED')
            },
            {
                percentage: `${findPercentage('approved')}%`,
                color: "var(--sea-100)",
                label: t('APPROVED')
            }];

        } else if (overallProgress === 'implementaion') {

            // Open (based on finding status): Number of ‘Open action pending’ + number of ‘Open - in process’ + number of ‘Evidence review pending’
            const openCount = findings?.filter(f => (f.status.toLowerCase() === 'open_action_pending' || f.status.toLowerCase() === 'open_in_process' || f.status.toLowerCase() === 'evidence_review_pending')).length;

            // Closed/Tentatively closed (based on finding status): Number of ‘Tentatively closed - onsite verification required' + number of ‘Closed - desktop verified’
            const closedOrTentativelyClosedCount = findings?.filter(f => (f.status.toLowerCase() === 'tentatively_closed_onsite_verification_required' || f.status.toLowerCase() === 'closed_desktop_verified')).length;

            setCapProgressDetails({
                openCount,
                approvedOrClosedLabel: 'CAP_CLOSED_OR_TENTATIVELY_CLOSED',
                approvedOrClosedCount: closedOrTentativelyClosedCount
            });

            progressBarArr = [{
                percentage: `${findPercentage('open_action_pending')}%`,
                color: "var(--high-orange)",
                label: `${t('OPEN')} \n (${t('ACTION_PENDING')})`
            },
            {
                percentage: `${findPercentage('open_in_process')}%`,
                color: "var(--medium-yellow)",
                label: `${t('OPEN')} \n (${t('IN_PROCESS')})`
            },
            {
                percentage: `${findPercentage('tentatively_closed_onsite_verification_required')}%`,
                color: "var(--cyan-green)",
                label: `${t('CAP_TENTATIVELY_CLOSED')} \n (${t('CAP_ONSITE_VERIFICATION_REQUIRED')})`
            },
            {
                percentage: `${findPercentage('closed_desktop_verified')}%`,
                color: "var(--dark-green-100)",
                label: `${t('CLOSED')} \n (${t('CAP_DESKTOP_VERIFIED')})`
            },
            {
                percentage: `${findPercentage('evidence_review_pending')}%`,
                color: "var(--ph-aqua-100)",
                label: t('EVIDENCE_REVIEW_PENDING')
            }];
        }
        setCapProgressBar(progressBarArr);
    }

    const showDevelopmentProgress = jobStatus === CAP_DEVELOPMENT || capType === DEVELOPMENT_SUPPORT;
    const showImplementationProgress = jobStatus === CAP_IMPLEMENTATION || jobStatus === CAP_COMPLETED || CAP_PROCESS_COMPLETE.includes(overallCapStatus);

    useEffect(() => {
        let overallProgress = '';
        let overallProgressTitle = 'CAP_PROGRESS';

        if (showDevelopmentProgress) {
            overallProgress = 'development';
            overallProgressTitle = 'CAP_DEVELOPMENT_PROGRESS';
            updateCapProgress(overallProgress);
        } else if (showImplementationProgress) {
            overallProgress = 'implementaion';
            overallProgressTitle = 'CAP_IMPLEMENTATION_PROGRESS';
            updateCapProgress(overallProgress);
        }
        setOverallCapProgress(overallProgress);
        setOverallCapProgressTitle(overallProgressTitle);

    }, [jobStatus]);

    useEffect(() => {
        if (overallCapProgress !== '') updateCapProgress(overallCapProgress);
    }, [findings]);

    return (
        <s.CapboxProgressWrapper>
            <s.CapboxProgressHeader>
                <s.CapboxProgressTitleDescription>
                    <s.CapboxProgressTitle>{t(overallCapProgressTitle)}</s.CapboxProgressTitle>
                    <s.CapboxProgressDescriptionBoxWrapper>
                        <s.CapboxProgressDescriptionBox>
                            <s.CapboxProgressDescription>
                                {t('CAP_AREAS_FOR_IMPROVEMENT')}
                            </s.CapboxProgressDescription>
                            <s.CapboxProgressDescriptionCount>{findings?.length}</s.CapboxProgressDescriptionCount>
                        </s.CapboxProgressDescriptionBox>
                        <s.CapboxProgressDescriptionBox>
                            <s.CapboxProgressDescription>
                                {jobStatus === CAP_DEVELOPMENT ? t('CAP_PROGRESS_OPEN') : t('OPEN')}
                            </s.CapboxProgressDescription>
                            <s.CapboxProgressDescriptionCount>{capProgressDetails.openCount}</s.CapboxProgressDescriptionCount>
                        </s.CapboxProgressDescriptionBox>
                        <s.CapboxProgressDescriptionBox>
                            <s.CapboxProgressDescription>
                                {t(capProgressDetails.approvedOrClosedLabel)}
                            </s.CapboxProgressDescription>
                            <s.CapboxProgressDescriptionCount>{capProgressDetails.approvedOrClosedCount}</s.CapboxProgressDescriptionCount>
                        </s.CapboxProgressDescriptionBox>
                    </s.CapboxProgressDescriptionBoxWrapper>
                </s.CapboxProgressTitleDescription>
                <s.CapboxProgressBar>
                    <ProgressLine visualParts={capProgressBar}></ProgressLine>
                </s.CapboxProgressBar>
            </s.CapboxProgressHeader>
        </s.CapboxProgressWrapper>
    )
};

export default React.memo(CapboxProgress);
